import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import { useRouter } from 'next/compat/router';
import { Button } from '@pass-frontend/abyss/common/components/button/ui';
import { ContentWidthWrapper } from '@pass-frontend/abyss/common/components/content-width-wrapper/ui';
import { MEDIA_QUERY } from '@pass-frontend/abyss/utility/style-helpers';
import {
  getRegistrationUrl,
  STANDALONE_PATHS,
} from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { env } from '@pass-frontend/features/configuration/util';
import { useStandaloneBreakpoints } from '@pass-frontend/features/standalone/data-bridge';

const FLEX_CSS = {
  'abyss-flex-root': {
    maxWidth: '635px',
    [MEDIA_QUERY.desktop]: {
      height: '339px',
    },
  },
};

const StyledWrapper = styled(Flex, {
  '&.abyss-flex-root': {
    flexWrap: 'nowrap',
    marginTop: '40px',
    [MEDIA_QUERY.mobileAndTablet]: {
      margin: '32px $sm 0 $sm',
    },
  },
});

const StyledImage = styled('img', {
  height: '560px',
  [MEDIA_QUERY.mobileAndTablet]: {
    marginTop: '$lg',
    width: '327px',
    height: 'auto',
  },
});

const StyledHeading = styled(Heading, {
  '&.abyss-heading-root': {
    fontWeight: '$normal',
    fontSize: '42px',
    marginBottom: '$lg',
    [MEDIA_QUERY.mobileAndTablet]: {
      marginBottom: '$md',
      fontSize: '38px',
    },
  },
});

const StyledDescription = styled(Text, {
  marginBottom: '$lg',
  textAlign: 'left',
});

const StyledButton = styled(Button, {
  [MEDIA_QUERY.mobileAndTablet]: {
    width: '100%',
  },
});

const BUTTONS_CSS = {
  columnGap: '$lg',
  [MEDIA_QUERY.mobileAndTablet]: {
    rowGap: '$md',
    width: '100%',
  },
};

export function HeroComponent() {
  const { t } = useIntl();
  const router = useRouter();
  const { isDesktop } = useStandaloneBreakpoints();
  const { FF_ENABLE_ELIGIBILITY_CHECK } = env();

  const handleGetStartedButtonClick = () => {
    router?.push(getRegistrationUrl(router?.locale));
  };

  const handleCheckEligibilityButtonClick = () => {
    router?.push(STANDALONE_PATHS.ELIGIBILITY_CHECK);
  };

  return (
    <Box color="$accent2" padding="0" height={isDesktop ? 600 : 'auto'}>
      <ContentWidthWrapper>
        <StyledWrapper
          direction={isDesktop ? 'row' : 'column'}
          justify={isDesktop ? 'space-between' : 'center'}
          alignItems="center"
        >
          <Flex
            direction="column"
            alignItems="flex-start"
            css={FLEX_CSS}
            justify="flex-start"
          >
            <StyledHeading color="$secondary2" offset={0} textAlign="left">
              {t('hero.new.heading')}
            </StyledHeading>
            <StyledDescription color="$secondary2" size="$lg">
              {t('hero.new.description')}
            </StyledDescription>
            <Flex
              direction="row"
              alignItems="flex-start"
              justify="flex-start"
              css={BUTTONS_CSS}
            >
              <StyledButton
                onClick={handleGetStartedButtonClick}
                data-ui-element-name="hero-component-get-started-btn"
                size="$lg"
              >
                {t('hero.new.cta')}
              </StyledButton>
              {FF_ENABLE_ELIGIBILITY_CHECK && (
                <StyledButton
                  onClick={handleCheckEligibilityButtonClick}
                  data-ui-element-name="hero-component-check-eligibility-btn"
                  size="$lg"
                  variant="outline"
                >
                  {t('header.checkEligibility')}
                </StyledButton>
              )}
            </Flex>
          </Flex>
          <StyledImage src="images/hero.jpg" alt={t('hero.imgAlt')} />
        </StyledWrapper>
      </ContentWidthWrapper>
    </Box>
  );
}
